import 'pure-react-carousel/dist/react-carousel.es.css';

import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Slide,
  Slider,
} from 'pure-react-carousel';
import React, { memo, useRef } from 'react';

import AddItemBox from '@/components/common/AddItemBox/AddItemBox';
import { Card } from '@/components/goalbased/Card/Card';
import { Delete } from '@/components/goalbased/Card/Delete/Delete';
import { Swap } from '@/components/goalbased/Card/Swap/Swap';
import { useMediaQuery } from '@/hooks';
import type { Cart } from '@/models/cart/cart';
import type { LineItem, OrderPlan } from '@/models/cart/order-plan';
import type { CalorieKey } from '@/utils/constants';
import { Action, days, SwapCategory } from '@/utils/constants';

import { CalorieModal } from '../../CalorieModal/CalorieModal';
import { RemoveItemModel } from '../../RemoveItemModel/RemoveItemModel';
import NutritionalInfo from '../NutritionalInfo/NutritionalInfo';

type DayProps = {
  active?: any;
  config: string[];
  day: number;
  items: LineItem[];
  orderPlan: OrderPlan;
  draftCart: Cart;
  swapConfig: any;
  setSwapCategory: (category: SwapCategory) => void;
};

type Category = 'brekkie' | 'snacks' | 'default';

const RemoveModalText: Record<
  Category,
  { title: string; description: string }
> = {
  brekkie: {
    title:
      'Remove Having a nutritious breakfast is important for sustaining energy levels and managing your hunger throughout the day.',
    description: 'Do you want to keep or remove your breakfast item?',
  },
  snacks: {
    title:
      'Incorporating 1-2 nutritious snacks into your daily routine can assist in sustaining your energy levels and to fuel you through the day.',
    description: 'Do you want to keep or remove your snack item?',
  },
  default: {
    title: 'Remove',
    description: 'Do you want to keep or remove your item?',
  },
};

const MealsPlan: React.FC<DayProps> = observer(
  ({
    active,
    config,
    day,
    items,
    orderPlan,
    setSwapCategory,
    draftCart,
    swapConfig,
  }) => {
    const router = useRouter();
    const categoryRefs = useRef<(HTMLDivElement | null)[]>(
      new Array(days.length).fill(null),
    );
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedCategory, setSelectedCategory] =
      React.useState<Category>('default');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [deletedPosition, setDeletedPosition] = React.useState(0);
    const dailyNutrition = orderPlan?.getNutritionByDay(day);
    const goal = orderPlan?.findAttribute('goal');
    const recommendedCal = orderPlan?.findAttribute('calorie');
    const actualCal = Number(dailyNutrition?.cal);
    const aboveThreshold = actualCal > recommendedCal * 1.1;
    const belowThreshold = actualCal < recommendedCal * 0.9;
    const calorieKey: CalorieKey = aboveThreshold
      ? 'aboveThreshold'
      : 'belowThreshold';

    const calorieModalContent = {
      aboveThreshold: {
        customContent: (
          <div className="mb-6">
            The total of your{' '}
            <span className="font-interSemibold">Day 0{day} calories</span> has
            exceeded your recommended {recommendedCal} daily calorie intake.
          </div>
        ),
      },
      belowThreshold: {
        customContent: (
          <div className="mb-6">
            <p className="mb-4">
              The total of your{' '}
              <span className="font-interSemibold">Day 0{day} calories</span> is
              below your recommended {recommendedCal} daily calorie intake.
            </p>
            <p>
              Consider adding an extra meal, snack, or drink to reach your
              recommended daily target.
            </p>
          </div>
        ),
      },
    };

    const selectedCalorieModalContent = calorieModalContent[calorieKey];

    const showToolTipPlans = ['caloriecontrol', 'weightloss'];
    const showToolTip =
      (showToolTipPlans.includes(goal as string) && aboveThreshold) ||
      belowThreshold;

    // For extra product
    const dailyLength = config.length;
    const extras = _.filter(
      items,
      (item: LineItem) => item.position >= dailyLength,
    );
    const dailyX = _.map(items, (item: LineItem) => item.position ?? 0);
    const extraX = Math.max(...dailyX, dailyLength - 1) + 1;

    const handleSwapOrAdd = (
      position: number,
      category: SwapCategory,
      action: Action,
    ) => {
      orderPlan.setXandYPosition(position, day);
      orderPlan.setScrollPosition(window.scrollY, router.pathname); // Save scroll position
      setSwapCategory(category);
      draftCart.setAction(action);
      router.push({
        pathname: '/swap',
        query: { ...(draftCart.mode && { mode: draftCart.mode }) },
      });
    };

    const handleToolTipClick = () => {
      setIsModalOpen(true);
    };

    const removeLineItem = (position: number) => {
      orderPlan.removeLineItem(position, day);
    };

    const handleDelete = (position: number, category?: Category) => {
      setDeletedPosition(position);
      const hideDeleteModal = sessionStorage.getItem('hideDeleteModal');
      if (hideDeleteModal === 'true' || !category) {
        removeLineItem(position);
      } else {
        setIsDeleteModalOpen(true);
        setSelectedCategory(category);
      }
    };

    const handleProductImageClick = (slug: string | undefined) => {
      orderPlan.setScrollPosition(window.scrollY, router.pathname); // Save scroll position
      router.push({
        pathname: `/products/${slug}`,
        query: {
          action: Action.VIEW,
          ...(draftCart.mode && { mode: draftCart.mode }),
        },
      });
    };

    const isMediumDevice = useMediaQuery(
      `(min-width:768px) and (max-width:1023px)`,
    );
    const isDesktopSM = useMediaQuery(
      `(min-width:1024px) and (max-width:1200px)`,
    );
    const isDesktopLG = useMediaQuery(
      `(min-width:1201px) and (max-width: 1535px)`,
    );
    const isXL = useMediaQuery(`(min-width: 1536px)`);

    let height: any;
    let slides: any;
    if (isMediumDevice) {
      height = 160;
      slides = 3.5;
    } else if (isDesktopSM) {
      height = 155;
      slides = 4.5;
    } else if (isDesktopLG) {
      height = 155;
      slides = 5.5;
    } else if (isXL) {
      height = 145;
      slides = 5.5;
    } else {
      height = 152;
      slides = 5.5;
    }

    const currentSlide = () => {
      const position = orderPlan.selectedItemPosition.x + 1;
      const totalSlides = config.length + extras.length + 1;
      if (position > slides) {
        return totalSlides - slides;
      }
      return position - 1;
    };

    return (
      <>
        <div
          key={day}
          className={`py-9 ${day !== active && 'hidden md:block'}`}
          ref={(el: any) => {
            categoryRefs.current[day] = el;
          }}
        >
          <div className="flex flex-row justify-between">
            <div className="hidden flex-row items-center md:flex">
              <div className="">
                <p className="font-inter text-2xl leading-8">
                  Day <span className="font-interMedium">0{day}</span>
                </p>
              </div>
              <button
                className="flex cursor-pointer flex-row items-center pl-6 text-attention"
                onClick={() =>
                  handleSwapOrAdd(extraX, SwapCategory.EXTRA, Action.ADD_EXTRA)
                }
              >
                <div className="w-4 ">+</div>
                <span className="font-inter text-base leading-4">
                  Add extra Item
                </span>
              </button>
            </div>
            <NutritionalInfo
              dailyNutrition={dailyNutrition}
              calorieKey={calorieKey}
              showToolTip={showToolTip}
              handleClick={handleToolTipClick}
            />
          </div>
          <hr className="hidden md:block" />
          <div
            className="hidden w-full flex-row flex-wrap py-6 md:flex"
            id={`day_${day}`}
          >
            <CarouselProvider
              naturalSlideWidth={100}
              naturalSlideHeight={height}
              totalSlides={config.length + extras.length + 1}
              visibleSlides={slides}
              className="
              relative w-full md:overflow-hidden md:after:pointer-events-none
              md:after:absolute md:after:right-0 md:after:top-0
              md:after:z-[1] md:after:h-full md:after:w-[90px]
              md:after:bg-gradient-to-r md:after:from-white/0
              md:after:to-white
              md:after:content-['']"
              currentSlide={
                orderPlan.selectedItemPosition.y === day ? currentSlide() : 0
              }
            >
              <Slider>
                {config.map((category: string, x: number) => {
                  const displayCategories = category;
                  const displayName =
                    _.find(swapConfig?.swapCategory, { key: category })?.name ??
                    category;
                  const found = _.find(items, (item: LineItem) => {
                    const itemPosition = item.position;
                    return itemPosition === x;
                  });
                  const product = found?.product;

                  return (
                    <Slide
                      key={day + x}
                      index={day + x}
                      className="w-1/6 first:ml-0 last:mr-0"
                    >
                      <div className="mx-4 h-full">
                        {product ? (
                          <Card
                            imageSrc={product.cms.tileImage?.url}
                            title={product.name || ''}
                            price={product.getDiscountedPrice(
                              orderPlan?.discount,
                            )}
                            wasPrice={product.grossPrice}
                            productType={displayName}
                            weight={product.nutritionalInfo.size}
                            unit={product.nutritionalInfo.unit}
                            titleStyle={'h-[44px]'}
                            customStyle={true}
                            onClick={() =>
                              handleProductImageClick(product.slug)
                            }
                            nutritionalInfo={product.nutritionalInfo}
                          >
                            <div className="my-2 flex w-full justify-between">
                              <Swap
                                onClick={() =>
                                  handleSwapOrAdd(
                                    x,
                                    displayCategories as SwapCategory,
                                    Action.SWAP_ITEM,
                                  )
                                }
                              />
                              {displayCategories !== 'meals' && (
                                <Delete
                                  onClick={() =>
                                    handleDelete(
                                      x,
                                      displayCategories as Category,
                                    )
                                  }
                                />
                              )}
                            </div>
                          </Card>
                        ) : (
                          <AddItemBox
                            onClick={() =>
                              handleSwapOrAdd(
                                x,
                                displayCategories as SwapCategory,
                                Action.ADD_ITEM,
                              )
                            }
                            label={`Add ${config[x]}`}
                            className={
                              config[x] === 'meals' ? 'text-error' : ''
                            }
                          />
                        )}
                      </div>
                    </Slide>
                  );
                })}

                {extras.map((extra) => {
                  const product = extra?.product;
                  const x = extra.position ?? dailyLength;
                  return (
                    <Slide
                      key={day + x}
                      index={day + x}
                      className="w-1/6 first:ml-0 last:mr-0"
                    >
                      <div className="mx-4 h-full">
                        {product && (
                          <Card
                            imageSrc={product.cms.tileImage?.url}
                            title={product.name || ''}
                            price={product.getDiscountedPrice(
                              orderPlan?.discount,
                            )}
                            wasPrice={product.grossPrice}
                            productType={SwapCategory.EXTRA}
                            weight={product.nutritionalInfo.size}
                            unit={product.nutritionalInfo.unit}
                            titleStyle={'h-[44px]'}
                            customStyle={true}
                            onClick={() =>
                              handleProductImageClick(product.slug)
                            }
                            nutritionalInfo={product.nutritionalInfo}
                          >
                            <div className="my-2 flex w-full justify-between">
                              <Swap
                                onClick={() =>
                                  handleSwapOrAdd(
                                    x,
                                    SwapCategory.EXTRA,
                                    Action.SWAP_ITEM,
                                  )
                                }
                              />
                              <Delete onClick={() => handleDelete(x)} />
                            </div>
                          </Card>
                        )}
                      </div>
                    </Slide>
                  );
                })}
                <Slide
                  key={day + extraX}
                  index={day + extraX}
                  className="w-1/6 first:ml-0 last:mr-0"
                >
                  <div className="mx-4 h-full">
                    <AddItemBox
                      onClick={() =>
                        handleSwapOrAdd(
                          extraX,
                          SwapCategory.EXTRA,
                          Action.ADD_EXTRA,
                        )
                      }
                      label={'Add Extra'}
                    />
                  </div>
                </Slide>
              </Slider>
              <ButtonBack className="absolute left-[-19px] top-0 h-full disabled:hidden md:pl-2">
                <Image
                  src="/assets/icons/next-button.svg"
                  alt="Previous icon"
                  style={{ transform: 'scaleX(-1)' }}
                  width={48}
                  height={48}
                />
              </ButtonBack>
              <ButtonNext className="absolute right-[-19px] top-0 z-10 h-full disabled:hidden md:pr-2">
                <Image
                  src="/assets/icons/next-button.svg"
                  alt="Next icon"
                  width={48}
                  height={48}
                />
              </ButtonNext>
            </CarouselProvider>
          </div>
          <div className="md:hidden">
            {config.map((category: string, x: number) => {
              const displayCategories = category;
              const displayName =
                _.find(swapConfig?.swapCategory, { key: category })?.name ??
                category;
              const found = _.find(items, (item: LineItem) => {
                const itemPosition = item.position;
                return itemPosition === x;
              });

              const product = found?.product;
              return (
                <div className="my-4" key={x}>
                  {product ? (
                    <Card
                      imageSrc={product.cms.tileImage?.url}
                      title={product.name || ''}
                      price={product.getDiscountedPrice(orderPlan?.discount)}
                      wasPrice={product.grossPrice}
                      productType={displayName}
                      weight={product.nutritionalInfo.size}
                      unit={product.nutritionalInfo.unit}
                      onClick={() => handleProductImageClick(product.slug)}
                      nutritionalInfo={product.nutritionalInfo}
                    >
                      <div className="my-2 flex w-full justify-between">
                        <Swap
                          onClick={() =>
                            handleSwapOrAdd(
                              x,
                              displayCategories as SwapCategory,
                              Action.SWAP_ITEM,
                            )
                          }
                        />
                        {displayCategories !== 'meals' && (
                          <div className="float-left pt-1">
                            <Delete
                              onClick={() =>
                                handleDelete(x, displayCategories as Category)
                              }
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  ) : (
                    <AddItemBox
                      onClick={() =>
                        handleSwapOrAdd(
                          x,
                          displayCategories as SwapCategory,
                          Action.ADD_ITEM,
                        )
                      }
                      label={`Add ${config[x]}`}
                      className={config[x] === 'meals' ? 'text-error' : ''}
                    />
                  )}
                </div>
              );
            })}
            {extras.map((extra) => {
              const product = extra?.product;
              // @ts-ignore
              const x = extra?.position ?? dailyLength;
              return (
                <div className="my-4" key={x}>
                  {product && (
                    <Card
                      imageSrc={product.cms.tileImage?.url}
                      title={product.name || ''}
                      price={product.getDiscountedPrice(orderPlan?.discount)}
                      wasPrice={product.grossPrice}
                      productType={SwapCategory.EXTRA}
                      weight={product.nutritionalInfo.size}
                      unit={product.nutritionalInfo.unit}
                      onClick={() => handleProductImageClick(product.slug)}
                      nutritionalInfo={product.nutritionalInfo}
                    >
                      <div className="my-2 flex w-full justify-between">
                        <Swap
                          onClick={() =>
                            handleSwapOrAdd(
                              x,
                              SwapCategory.EXTRA,
                              Action.SWAP_ITEM,
                            )
                          }
                        />
                        <Delete onClick={() => handleDelete(x)} />
                      </div>
                    </Card>
                  )}
                </div>
              );
            })}
          </div>
          <button
            id={`add-extra-button-${day}`}
            className={`mt-1 flex w-full items-center justify-center rounded-md border border-dashed border-grey-dark	bg-off-white py-5 font-interMedium text-base leading-4 text-attention ${
              day !== active ? 'hidden md:hidden' : 'block md:hidden'
            }`}
            onClick={() =>
              handleSwapOrAdd(extraX, SwapCategory.EXTRA, Action.ADD_EXTRA)
            }
          >
            <span className="w-4 ">+</span>
            Add an extra item
          </button>
          <CalorieModal
            isOpen={isModalOpen}
            onRequestClose={() => setIsModalOpen(false)}
          >
            {selectedCalorieModalContent.customContent}
          </CalorieModal>
          <RemoveItemModel
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onRemove={() => {
              removeLineItem(deletedPosition);
              setIsDeleteModalOpen(false);
            }}
            title={RemoveModalText[selectedCategory]?.title || ''}
            description={RemoveModalText[selectedCategory]?.description || ''}
          />
        </div>
      </>
    );
  },
);

export default memo(MealsPlan);
